import React, { useState } from 'react';
import { FilterIcon, DownloadIcon, CalendarIcon, CreditCardIcon, MonitorIcon } from 'lucide-react';
import { DashboardKPIs } from '../components/dashboard/DashboardKPIs';
import { MerchantStats } from '../components/dashboard/MerchantStats';
import { TransactionsByChannel } from '../components/dashboard/TransactionsByChannel';
import { TransactionsByScheme } from '../components/dashboard/TransactionsByScheme';
import { RegionalPerformance } from '../components/dashboard/RegionalPerformance';
import { TopMerchants } from '../components/dashboard/TopMerchants';
import { TransactionTrends } from '../components/dashboard/TransactionTrends';
import { SchemePerformance } from '../components/dashboard/SchemePerformance';
import { RiskMonitoring } from '../components/dashboard/RiskMonitoring';
import { ActivityFeed } from '../components/dashboard/ActivityFeed';
export type TimeRange = 'Today' | 'Week' | 'MTD' | 'YTD';
export type SchemeFilter = 'All Schemes' | 'Visa' | 'MasterCard' | 'UnionPay' | 'PayPak' | 'RAAST';
export type ChannelFilter = 'All Channels' | 'POS' | 'E-commerce' | 'RAAST QR';
export interface DashboardFilters {
  timeRange: TimeRange;
  scheme: SchemeFilter;
  channel: ChannelFilter;
}
const Dashboard: React.FC = () => {
  const [timeRange, setTimeRange] = useState<TimeRange>('Today');
  const [scheme, setScheme] = useState<SchemeFilter>('All Schemes');
  const [channel, setChannel] = useState<ChannelFilter>('All Channels');
  const filters: DashboardFilters = {
    timeRange,
    scheme,
    channel
  };
  return <div className="space-y-8 pb-12 animate-in fade-in duration-500" data-id="element-3597">
      {/* Header & Global Filters */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 bg-white dark:bg-slate-800/50 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700/50" data-id="element-3598">
        <div data-id="element-3599">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight" data-id="element-3600">
            Platform Overview
          </h1>
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-1.5" data-id="element-3601">
            Enterprise merchant management and transaction analytics
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4" data-id="element-3602">
          {/* Time Period Toggle */}
          <div className="flex p-1 bg-slate-100 dark:bg-slate-900/50 rounded-xl border border-slate-200/50 dark:border-slate-700/50 shadow-inner" data-id="element-3603">
            {(['Today', 'Week', 'MTD', 'YTD'] as const).map(range => <button key={range} onClick={() => setTimeRange(range)} className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${timeRange === range ? 'bg-white dark:bg-slate-800 text-accent-600 dark:text-accent-400 shadow-sm ring-1 ring-slate-200 dark:ring-slate-700' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50'}`} data-id="element-3604">
                {range}
              </button>)}
          </div>

          {/* Additional Filters */}
          <div className="flex items-center gap-3" data-id="element-3605">
            <div className="relative group" data-id="element-3606">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-3607">
                <CreditCardIcon className="h-4 w-4 text-slate-400 group-focus-within:text-accent-500 transition-colors" data-id="element-3608" />
              </div>
              <select value={scheme} onChange={e => setScheme(e.target.value as SchemeFilter)} className="appearance-none text-sm font-medium border border-slate-200 dark:border-slate-700 rounded-xl py-2.5 pl-9 pr-10 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500 shadow-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors" data-id="element-3609">
                <option value="All Schemes" data-id="element-3610">All Schemes</option>
                <option value="Visa" data-id="element-3611">Visa</option>
                <option value="MasterCard" data-id="element-3612">MasterCard</option>
                <option value="UnionPay" data-id="element-3613">UnionPay</option>
                <option value="PayPak" data-id="element-3614">PayPak</option>
                <option value="RAAST" data-id="element-3615">RAAST</option>
              </select>
            </div>

            <div className="relative group hidden sm:block" data-id="element-3616">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-3617">
                <MonitorIcon className="h-4 w-4 text-slate-400 group-focus-within:text-accent-500 transition-colors" data-id="element-3618" />
              </div>
              <select value={channel} onChange={e => setChannel(e.target.value as ChannelFilter)} className="appearance-none text-sm font-medium border border-slate-200 dark:border-slate-700 rounded-xl py-2.5 pl-9 pr-10 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500 shadow-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors" data-id="element-3619">
                <option value="All Channels" data-id="element-3620">All Channels</option>
                <option value="POS" data-id="element-3621">POS</option>
                <option value="E-commerce" data-id="element-3622">E-commerce</option>
                <option value="RAAST QR" data-id="element-3623">RAAST QR</option>
              </select>
            </div>

            <button onClick={() => {
            setTimeRange('Today');
            setScheme('All Schemes');
            setChannel('All Channels');
          }} className="p-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-500 hover:text-accent-600 dark:text-slate-400 dark:hover:text-accent-400 hover:bg-accent-50 dark:hover:bg-accent-900/20 hover:border-accent-200 dark:hover:border-accent-800 shadow-sm transition-all" title="Reset Filters" data-id="element-3624">
              <FilterIcon className="h-4 w-4" data-id="element-3625" />
            </button>
          </div>
        </div>
      </div>

      {/* Row 1: KPI Cards */}
      <DashboardKPIs filters={filters} data-id="element-3626" />

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8" data-id="element-3627">
        {/* Left Column */}
        <div className="xl:col-span-2 space-y-8" data-id="element-3628">
          {/* Row 2: Merchant Stats & Scheme Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-3629">
            <MerchantStats filters={filters} data-id="element-3630" />
            <TransactionsByScheme filters={filters} data-id="element-3631" />
          </div>

          {/* Row 3: Transaction Channels */}
          <TransactionsByChannel filters={filters} data-id="element-3632" />

          {/* Row 4: Transaction Trends */}
          <TransactionTrends filters={filters} data-id="element-3633" />

          {/* Row 5: Scheme Performance & Risk */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-3634">
            <SchemePerformance filters={filters} data-id="element-3635" />
            <RiskMonitoring filters={filters} data-id="element-3636" />
          </div>

          {/* Row 6: Regional Performance */}
          <RegionalPerformance filters={filters} data-id="element-3637" />

          {/* Row 7: Top Merchants */}
          <TopMerchants filters={filters} data-id="element-3638" />
        </div>

        {/* Right Column */}
        <div className="xl:col-span-1" data-id="element-3639">
          <div className="sticky top-24 h-[calc(100vh-140px)]" data-id="element-3640">
            <ActivityFeed filters={filters} data-id="element-3641" />
          </div>
        </div>
      </div>
    </div>;
};
export default Dashboard;